// Builds the text grammar Vosk constrains a phrase to. Everything this module
// produces is handed to `new model.KaldiRecognizer(rate, grammar)`, whose grammar
// argument is an UNWEIGHTED phrase acceptor (a JSON array of phrase strings, plus
// `[unk]`). Vosk composes it with the model's HCLr lookahead graph in memory at
// recognizer construction.
//
// IMPORTANT: the text-grammar path carries NO language-model weights and NO word
// alternatives. It can only constrain decoding to a set of literal phrases. Neither
// `opts.graph` nor `wordList` here is the biased LM:
//   1. opts.graph — a prebuilt grammar descriptor (serialized grammar JSON, or an
//      array of phrase strings). It wins over `wordList`, but it is still just an
//      unweighted acceptor: KaldiRecognizer's text grammar API cannot carry KN
//      n-gram weights or alternatives.
//   2. wordList — a flat phrase acceptor derived from the expected reading text.
// Real per-story biasing (Kneser-Ney n-gram from amira-lm-build; the live corpus is
// deliberately no-alternatives) only takes effect when the binary OpenFST `HCLG.fst`
// IS the recognizer's decode graph. That path is orchestrated by
// ./voskBiasedGraph.js and is LIVE end-to-end wherever the vosk backend is selected
// — there is no separate flag — on the runtime this client resolves (asset
// `runtime/3.0.1` = vosk-v2.2 plus the split-bundle worker and the per-recognizer
// graph binding), via the one-step `new KaldiRecognizer(sr, { graph })`.
//
// Callers fall back HERE when that binding is absent: stock vosk-browser 0.0.8
// cannot inject an FST at all, and the v2.x `Model.loadGraph` staging API is banned
// even though it exists — it re-parses the acoustic model per swap and strands
// ~33 MB of wasm heap each time (lexa-asr-assets#33), so VoskAsr keeps
// `_biasedGraphRuntime` false on v2.x and throws BiasedGraphUnsupportedError rather
// than using it. Falling back is not free either: on zoov models the word-list path
// has its own open defect (OpenFst toolchain mismatch), so per-phrase graphs are the
// good decode path.
//
// Empty/null input means unconstrained (free decoding against the model's Gr.fst).

// The tokenizer lives in ../wordNormalize.js because streaming_consensus.js builds
// its in-passage wordSet with the SAME transform from the SAME word list. Keeping a
// private copy here is what let the two drift (see that module's header).
import { normalizeWord } from '../wordNormalize';

/** Out-of-grammar audio decodes as [unk] instead of being force-fit to the phrase. */
const UNK_TOKEN = '[unk]';

/**
 * @param {string[]} wordList
 * @returns {string} Space-joined normalized phrase, or ''.
 */
function phraseFromWordList(wordList) {
  if (!Array.isArray(wordList)) {
    return '';
  }
  const tokens = [];
  for (const raw of wordList) {
    const norm = normalizeWord(raw);
    if (norm) {
      tokens.push(norm);
    }
  }
  return tokens.join(' ').trim();
}

/**
 * Normalizes a prebuilt graph descriptor to a grammar string: a JSON string is
 * returned verbatim, an array of phrases is JSON-encoded. Null when unusable.
 * @param {string|string[]} graph
 * @returns {string|null}
 */
function grammarFromGraph(graph) {
  if (typeof graph === 'string') {
    const trimmed = graph.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (Array.isArray(graph)) {
    const phrases = graph.map((p) => String(p || '').trim()).filter(Boolean);
    return phrases.length > 0 ? JSON.stringify(phrases) : null;
  }
  return null;
}

/**
 * Returns the grammar string for `new model.KaldiRecognizer(rate, grammar)`.
 * A prebuilt `graph` takes precedence over `wordList`. Null means free decoding.
 * @param {string[]} [wordList]
 * @param {string|string[]} [graph]
 * @returns {string|null}
 */
export function buildPhraseGrammar(wordList, graph) {
  const fromGraph = grammarFromGraph(graph);
  if (fromGraph) {
    return fromGraph;
  }

  const phrase = phraseFromWordList(wordList);
  if (!phrase) {
    return null;
  }
  return JSON.stringify([phrase, UNK_TOKEN]);
}

export const __VOSK_GRAMMAR_TESTS__ = { normalizeWord, phraseFromWordList, grammarFromGraph };
